/**
 * Accept a coach/partner sharing invite (T16.5 / ISSUE-081).
 *
 * Reached from the invite email link: /sharing/accept?token=…
 * Requires an authenticated session (RequireAuth). The grantee's email must
 * match the invite; the server enforces this and we surface a clear message.
 */
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router';
import { useAcceptCoachLink } from '../hooks/useSharing.js';

function messageForStatus(status: number | undefined, t: (k: string) => string): string {
  switch (status) {
    case 404:
      return t('grantee.errorNotFound');
    case 410:
      return t('grantee.errorRevoked');
    case 403:
      return t('grantee.errorMismatch');
    case 400:
      return t('grantee.errorSelf');
    default:
      return t('grantee.errorGeneric');
  }
}

export default function SharingAcceptPage() {
  const { t } = useTranslation('sharing');
  const [params] = useSearchParams();
  const accept = useAcceptCoachLink();
  const [manualToken, setManualToken] = useState('');
  const triedRef = useRef(false);

  const urlToken = params.get('token');

  // Auto-accept once if a token came in via the email link.
  useEffect(() => {
    if (urlToken && !triedRef.current) {
      triedRef.current = true;
      accept.mutate(urlToken);
    }
  }, [urlToken, accept]);

  const handleManual = (e: React.FormEvent) => {
    e.preventDefault();
    if (manualToken.trim()) accept.mutate(manualToken.trim());
  };

  return (
    <div className="flex max-w-md flex-col gap-4">
      <h2 className="text-xl font-medium tracking-tight">{t('grantee.acceptTitle')}</h2>

      {accept.isSuccess ? (
        <div className="flex flex-col gap-3">
          <p className="text-sm text-leaf">{t('grantee.accepted')}</p>
          <Link to="/sharing" className="self-start rounded bg-leaf px-4 py-1.5 text-sm text-white">
            {t('grantee.viewWeek')}
          </Link>
        </div>
      ) : (
        <>
          <p className="text-sm text-ink-soft">{t('grantee.acceptBodyGeneric')}</p>

          {urlToken && accept.isPending && (
            <p className="text-sm text-ink-soft">{t('grantee.accepting')}</p>
          )}

          {/* Manual token entry — fallback when no token in the URL or the link failed. */}
          {(!urlToken || accept.isError) && (
            <form onSubmit={handleManual} className="flex flex-col gap-2">
              <label className="flex flex-col gap-1">
                <span className="text-xs text-ink-soft">{t('grantee.tokenLabel')}</span>
                <input
                  type="text"
                  value={manualToken}
                  onChange={(e) => setManualToken(e.currentTarget.value)}
                  placeholder={t('grantee.tokenPlaceholder')}
                  className="rounded border border-husk px-2 py-1.5 text-sm text-ink"
                />
              </label>
              <button
                type="submit"
                disabled={accept.isPending || !manualToken.trim()}
                className="self-start rounded bg-leaf px-4 py-1.5 text-sm text-white disabled:opacity-50"
              >
                {accept.isPending ? t('grantee.accepting') : t('grantee.acceptSubmit')}
              </button>
            </form>
          )}

          {accept.isError && (
            <p className="text-sm text-clay" role="alert">
              {messageForStatus(accept.error?.status, t)}
            </p>
          )}
        </>
      )}
    </div>
  );
}
